<template>
  <section
    class="boxes-blank"
    :class="{ 'boxes-blank--unavailable': unavailable }"
    data-shape-group="3d-blocks"
    aria-labelledby="boxes-blank-heading"
  >
    <header>
      <button
        type="button"
        class="boxes-blank__toggle"
        :aria-expanded="expanded"
        @click="expanded = !expanded"
      >
        <h2 id="boxes-blank-heading">{{ headingLabel }}</h2>
        <svg
          class="panel-chevron"
          :class="{ 'panel-chevron--collapsed': !expanded }"
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M4.5 6.75L9 11.25L13.5 6.75"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </header>

    <div v-show="expanded" class="boxes-blank__content">
      <p v-if="unavailable" class="boxes-blank__subtitle boxes-blank__subtitle--unavailable">
        Shapes available in <button type="button" class="boxes-blank__grid-link" aria-haspopup="dialog"
          @mouseenter="highlightAvailableGrids([GRID_PATTERN_IDS.THREE_D])"
          @click="gridOptions([GRID_PATTERN_IDS.THREE_D])">3D Grid</button>
      </p>
      <p v-else class="boxes-blank__subtitle">
        Change the Depth of the Blocks
      </p>

      <!-- Depth Tabs -->
      <div class="boxes-blank__depth-tabs" role="tablist" aria-label="Block Depth">
        <button
          type="button"
          role="tab"
          :aria-selected="selectedDepth === '2'"
          class="boxes-blank__depth-tab"
          :class="{ 'boxes-blank__depth-tab--active': selectedDepth === '2' }"
          @click="setDepth('2')"
        >
          <span class="boxes-blank__depth-text">2ʺ</span>
          <svg
            v-if="selectedDepth === '2'"
            class="boxes-blank__depth-chevron boxes-blank__depth-chevron--down"
            width="7"
            height="4"
            viewBox="0 0 7 4"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M0.5 0.5L3.5 3.5L6.5 0.5"
              stroke="currentColor"
              stroke-width="1.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <svg
            v-else
            class="boxes-blank__depth-chevron boxes-blank__depth-chevron--left"
            width="4"
            height="7"
            viewBox="0 0 4 7"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M3.5 0.5L0.5 3.5L3.5 6.5"
              stroke="currentColor"
              stroke-width="1.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>

        <button
          type="button"
          role="tab"
          :aria-selected="selectedDepth === '4'"
          class="boxes-blank__depth-tab"
          :class="{ 'boxes-blank__depth-tab--active': selectedDepth === '4' }"
          @click="setDepth('4')"
        >
          <span class="boxes-blank__depth-text">4ʺ</span>
          <svg
            v-if="selectedDepth === '4'"
            class="boxes-blank__depth-chevron boxes-blank__depth-chevron--down"
            width="7"
            height="4"
            viewBox="0 0 7 4"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M0.5 0.5L3.5 3.5L6.5 0.5"
              stroke="currentColor"
              stroke-width="1.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <svg
            v-else
            class="boxes-blank__depth-chevron boxes-blank__depth-chevron--left"
            width="4"
            height="7"
            viewBox="0 0 4 7"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M3.5 0.5L0.5 3.5L3.5 6.5"
              stroke="currentColor"
              stroke-width="1.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>

      <!-- Container box holding all sets -->
      <div class="boxes-blank__box">
        <!-- Sets of 2 -->
        <div class="boxes-blank__section">
          <p class="boxes-blank__row-label">(Sold in Sets of 2)</p>
          <div class="boxes-blank__grid-3x3">
            <div
              v-for="(shape, index) in setsOf2Shapes"
              :key="shape.id"
              class="boxes-blank__shape-cell"
            >
              <button
                type="button"
                class="boxes-blank__shape boxes-blank__shape--square"
                :class="{
                  'boxes-blank__shape--active': !unavailable && isSelected(shape.shapeId),
                  'boxes-blank__shape--unavailable': unavailable,
                }"
                :disabled="unavailable"
                :aria-disabled="unavailable ? 'true' : undefined"
                :aria-pressed="!unavailable && isSelected(shape.shapeId)"
                :aria-label="shape.label"
                :title="shape.label"
                data-block-preview
                :data-shape-id="shape.shapeId"
                @mouseenter="hoveredShapeId = shape.id"
                @mouseleave="hoveredShapeId = null"
                @click="selectShape(shape)"
              >
                <img :src="getShapeSvg(shape)" :alt="shape.label" class="boxes-blank__shape-svg">
              </button>

              <!-- Dropdown to select orientation -->
              <div
                v-if="!unavailable && shapeHasChildren(shape.shapeId) && activeDropdownShapeId === shape.shapeId"
                class="boxes-blank__children"
                :class="getDropdownAlignClass(index)"
                data-shape-children
              >
                <button
                  v-for="option in getShapeRotations(shape.shapeId)"
                  :key="option.id"
                  type="button"
                  class="boxes-blank__child-btn"
                  :class="{ 'boxes-blank__child-btn--active': selectedShape?.id === option.id }"
                  :aria-label="option.label"
                  :title="option.label"
                  :data-shape-id="option.id"
                  :data-child-rotation="option.rotation || 0"
                  @mouseenter="hoveredChildId = option.id"
                  @mouseleave="hoveredChildId = null"
                  @click.stop="selectOrientation(option)"
                >
                  <img
                    :src="getOptionSvg(shape, option)"
                    :alt="option.label"
                    class="boxes-blank__shape-svg"
                  >
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Sets of 4 -->
        <div class="boxes-blank__section">
          <p class="boxes-blank__row-label">(Sold in Sets of 4)</p>
          <div class="boxes-blank__row-single">
            <div class="boxes-blank__shape-cell boxes-blank__shape-cell--rect">
              <button
                type="button"
                class="boxes-blank__shape boxes-blank__shape--rect"
                :class="{
                  'boxes-blank__shape--active': !unavailable && isSelected(rectShape.shapeId),
                  'boxes-blank__shape--unavailable': unavailable,
                }"
                :disabled="unavailable"
                :aria-disabled="unavailable ? 'true' : undefined"
                :aria-pressed="!unavailable && isSelected(rectShape.shapeId)"
                :aria-label="rectShape.label"
                :title="rectShape.label"
                data-block-preview
                :data-shape-id="rectShape.shapeId"
                @mouseenter="hoveredShapeId = rectShape.id"
                @mouseleave="hoveredShapeId = null"
                @click="selectShape(rectShape)"
              >
                <img :src="getRectShapeSvg(rectShape)" :alt="rectShape.label" class="boxes-blank__shape-svg">
              </button>

              <!-- Dropdown for Rect12x36 orientation -->
              <div
                v-if="!unavailable && shapeHasChildren(rectShape.shapeId) && activeDropdownShapeId === rectShape.shapeId"
                class="boxes-blank__children boxes-blank__children--left"
                data-shape-children
              >
                <button
                  v-for="option in getShapeRotations(rectShape.shapeId)"
                  :key="option.id"
                  type="button"
                  class="boxes-blank__child-btn"
                  :class="{
                    'boxes-blank__child-btn--active': selectedShape?.id === option.id,
                    'boxes-blank__child-btn--rect-vert': !option.rotation,
                    'boxes-blank__child-btn--rect-horiz': option.rotation === 90,
                  }"
                  :aria-label="option.label"
                  :title="option.label"
                  :data-shape-id="option.id"
                  :data-child-rotation="option.rotation || 0"
                  @mouseenter="hoveredChildId = option.id"
                  @mouseleave="hoveredChildId = null"
                  @click.stop="selectOrientation(option)"
                >
                  <img
                    :src="getOptionSvg(rectShape, option)"
                    :alt="option.label"
                    class="boxes-blank__shape-svg"
                  >
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Sets of 8 -->
        <div class="boxes-blank__section">
          <p class="boxes-blank__row-label">(Sold in Sets of 8)</p>
          <div class="boxes-blank__row-single">
            <button
              type="button"
              class="boxes-blank__shape boxes-blank__shape--cube"
              :class="{
                'boxes-blank__shape--active': !unavailable && isSelected(cubeShape.shapeId),
                'boxes-blank__shape--unavailable': unavailable,
              }"
              :disabled="unavailable"
              :aria-disabled="unavailable ? 'true' : undefined"
              :aria-pressed="!unavailable && isSelected(cubeShape.shapeId)"
              :aria-label="cubeShape.label"
              :title="cubeShape.label"
              data-block-preview
              :data-shape-id="cubeShape.shapeId"
              @mouseenter="hoveredShapeId = cubeShape.id"
              @mouseleave="hoveredShapeId = null"
              @click="selectShape(cubeShape)"
            >
              <img :src="getShapeSvg(cubeShape)" :alt="cubeShape.label" class="boxes-blank__shape-svg">
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { checkRotationCollision, findNearbyNonCollidingPosition } from '~/components/CanvasGrid/helpers'
import { GRID_PATTERN_IDS, SELECTABLE_TOOLS } from '~/composables/grid/constants'
import { transformShapeForAdd } from '~/composables/shapes/transform'
import { getParentShape, getShapeById } from '~/composables/shapes/utils'
import { Toast } from '~/composables/tools'
import { ENGRAVED_3D_SHAPES, SHAPES as SHAPES_3D } from '~/composables/shapes/shapesFor3D'

const props = withDefaults(defineProps<{
  unavailable?: boolean
}>(), {
  unavailable: false,
})

const { gridOptions, highlightAvailableGrids } = useNewStudioActions()
const gridStore = useGridStore()
const { selectedShape } = storeToRefs(gridStore)

const expanded = ref(true)
const selectedDepth = ref<'2' | '4'>('2')
const hoveredShapeId = ref<string | null>(null)

const all3DShapes = [...SHAPES_3D, ...ENGRAVED_3D_SHAPES].flatMap(s => [s, ...(s.children || [])])

const activeDropdownShapeId = ref<string | null>(null)
const hoveredChildId = ref<string | null>(null)

const shapeHasChildren = (shapeId: string) => {
  const fullShape = all3DShapes.find(s => s.id === shapeId)
  return Boolean(fullShape?.children && fullShape.children.length > 0)
}

const getShapeRotations = (shapeId: string) => {
  const fullShape = all3DShapes.find(s => s.id === shapeId)
  if (!fullShape) return []
  return [fullShape, ...(fullShape.children || [])]
}

const isSelected = (shapeId: string) => {
  if (selectedShape.value?.id === shapeId) return true
  const fullShape = all3DShapes.find(s => s.id === shapeId)
  return Boolean(fullShape?.children?.some(c => c.id === selectedShape.value?.id))
}

const getActiveRotation = (shapeId: string) => {
  if (selectedShape.value?.id === shapeId) return selectedShape.value.rotation || 0
  const fullShape = all3DShapes.find(s => s.id === shapeId)
  const activeChild = fullShape?.children?.find(c => c.id === selectedShape.value?.id)
  return activeChild?.rotation || 0
}

const getOptionSvg = (shape: BoxItem, option: Shape) => {
  const rot = option.rotation || 0
  const rotSuffix = rot ? `_${rot}` : ''
  if (selectedShape.value?.id === option.id) {
    return shape.svgSelected.replace('.svg', `${rotSuffix}.svg`)
  }
  if (hoveredChildId.value === option.id) {
    return shape.svgHover.replace('.svg', `${rotSuffix}.svg`)
  }
  return shape.svgDefault.replace('.svg', `${rotSuffix}.svg`)
}

const getDropdownAlignClass = (index: number) => {
  if (index % 3 === 2) return 'boxes-blank__children--right'
  if (index % 3 === 1) return 'boxes-blank__children--center'
  return 'boxes-blank__children--left'
}

const headingLabel = computed(() => {
  const id = selectedShape.value?.id
  if (!id) return '3D BLOCKS - BLANK'
  const match = id.match(/Cube36x36x[24](LargeParallel|Tangent|Zen|River|Offset|Topo|Converge|Subway)/)
  return match ? '3D BLOCKS - ENGRAVED' : '3D BLOCKS - BLANK'
})

const setDepth = (depth: '2' | '4') => {
  if (selectedDepth.value === depth) return
  const prevDepth = selectedDepth.value
  selectedDepth.value = depth
  if (!props.unavailable && selectedShape.value?.id) {
    const currentId = selectedShape.value.id
    const newId = currentId.replace(new RegExp(`x${prevDepth}`), `x${depth}`)
    const matchingShape = all3DShapes.find(s => s.id === newId)
    if (matchingShape) {
      gridStore.setSelectedShape(matchingShape)
      gridStore.setTempItemByCurrentSelection()
    }
  }
}

// 9 shapes for "(Sold in Sets of 2)" in exact Figma order:
// 1. Blank, 2. Large Parallel, 3. Tangent, 4. Zen, 5. Offset, 6. River, 7. Topo, 8. Converge, 9. Subway
const SETS_OF_2_PATTERNS = [
  { id: 'Blank', label: '36"×36"', isBlank: true },
  { id: 'LargeParallel', label: 'Large Parallel', isBlank: false },
  { id: 'Tangent', label: 'Tangent', isBlank: false },
  { id: 'Zen', label: 'Zen', isBlank: false },
  { id: 'Offset', label: 'Offset', isBlank: false },
  { id: 'River', label: 'River', isBlank: false },
  { id: 'Topo', label: 'Topo', isBlank: false },
  { id: 'Converge', label: 'Converge', isBlank: false },
  { id: 'Subway', label: 'Subway', isBlank: false },
]

interface BoxItem {
  id: string
  shapeId: string
  label: string
  svgDefault: string
  svgHover: string
  svgSelected: string
}

const setsOf2Shapes = computed<BoxItem[]>(() => {
  const depth = selectedDepth.value
  return SETS_OF_2_PATTERNS.map((p) => {
    if (p.isBlank) {
      return {
        id: `Cube36x36x${depth}`,
        shapeId: `Cube36x36x${depth}Set`,
        label: `36"×36"×${depth}" (Set of 2)`,
        svgDefault: `/new-studio/shapes/3d-active/Cube36x36x${depth}.svg`,
        svgHover: `/new-studio/shapes/3d-hover/Cube36x36x${depth}.svg`,
        svgSelected: `/new-studio/shapes/3d-selected/Cube36x36x${depth}.svg`,
      }
    }
    return {
      id: `${p.id}${depth}`,
      shapeId: `Cube36x36x${depth}${p.id}`,
      label: `${p.label} ${depth}" (Set of 2)`,
      svgDefault: `/new-studio/shapes/3d-engraved-active/${p.id}${depth}.svg`,
      svgHover: `/new-studio/shapes/3d-engraved-hover/${p.id}${depth}.svg`,
      svgSelected: `/new-studio/shapes/3d-engraved-selected/${p.id}${depth}.svg`,
    }
  })
})

const rectShape = computed<BoxItem>(() => {
  const depth = selectedDepth.value
  return {
    id: `Rect12x36x${depth}`,
    shapeId: `Rect12x36x${depth}Set`,
    label: `12"×36"×${depth}" (Set of 4)`,
    svgDefault: `/new-studio/shapes/3d-active/Rect12x36x${depth}.svg`,
    svgHover: `/new-studio/shapes/3d-hover/Rect12x36x${depth}.svg`,
    svgSelected: `/new-studio/shapes/3d-selected/Rect12x36x${depth}.svg`,
  }
})

const cubeShape = computed<BoxItem>(() => {
  const depth = selectedDepth.value
  return {
    id: `Cube12x12x${depth}`,
    shapeId: `Cube12x12x${depth}Set`,
    label: `12"×12"×${depth}" (Set of 8)`,
    svgDefault: `/new-studio/shapes/3d-active/Cube12x12x${depth}.svg`,
    svgHover: `/new-studio/shapes/3d-hover/Cube12x12x${depth}.svg`,
    svgSelected: `/new-studio/shapes/3d-selected/Cube12x12x${depth}.svg`,
  }
})

const getShapeSvg = (shape: BoxItem) => {
  const rot = getActiveRotation(shape.shapeId)
  const rotSuffix = rot ? `_${rot}` : ''
  if (!props.unavailable && isSelected(shape.shapeId)) {
    return shape.svgSelected.replace('.svg', `${rotSuffix}.svg`)
  }
  if (!props.unavailable && hoveredShapeId.value === shape.id) {
    return shape.svgHover.replace('.svg', `${rotSuffix}.svg`)
  }
  return shape.svgDefault.replace('.svg', `${rotSuffix}.svg`)
}

const getRectShapeSvg = (shape: BoxItem) => {
  if (!props.unavailable && isSelected(shape.shapeId)) {
    return shape.svgSelected
  }
  if (!props.unavailable && hoveredShapeId.value === shape.id) {
    return shape.svgHover
  }
  return shape.svgDefault
}

const WOOD_GRAIN_LABELS = ['Dark Ash', 'Light Ash', 'Medium Elm', 'Natural Walnut', 'White Oak']

const isEngraved3DShape = (shapeId: string) => {
  return /Cube36x36x[24](LargeParallel|Tangent|Zen|River|Offset|Topo|Converge|Subway)/.test(shapeId)
}

const resetColorIfWoodGrain = () => {
  if (gridStore.selectedColor && WOOD_GRAIN_LABELS.includes(gridStore.selectedColor.label)) {
    const ebonyColor = useStudioColors().getColors().find(c => c.label === 'Ebony')
    if (ebonyColor) {
      gridStore.setSelectedColor(ebonyColor)
    }
  }
}

const selectShape = (shape: BoxItem) => {
  if (props.unavailable) return
  const fullShape = all3DShapes.find(s => s.id === shape.shapeId)
  if (!fullShape) return

  if (isEngraved3DShape(shape.shapeId)) {
    resetColorIfWoodGrain()
  }

  gridStore.updateSelectedTool(SELECTABLE_TOOLS.ADD)
  if (fullShape.children?.length) {
    activeDropdownShapeId.value = activeDropdownShapeId.value === shape.shapeId ? null : shape.shapeId
    if (!isSelected(shape.shapeId)) {
      gridStore.setSelectedShape(fullShape)
      try {
        gridStore.setTempItemByCurrentSelection()
      } catch (e) {
        console.error('[3D Blocks] setTempItemByCurrentSelection failed:', e)
      }
    }
  } else {
    activeDropdownShapeId.value = null
    gridStore.setSelectedShape(fullShape)
    try {
      gridStore.setTempItemByCurrentSelection()
    } catch (e) {
      console.error('[3D Blocks] setTempItemByCurrentSelection failed:', e)
    }
  }
}

const selectOrientation = async (optionShape: Shape) => {
  if (props.unavailable) return

  if (isEngraved3DShape(optionShape.id)) {
    resetColorIfWoodGrain()
  }

  // If exactly 1 item is selected on the grid, rotate ONLY that selected item
  if (gridStore.selectedItemsOnGrid.length === 1) {
    const selectedItem = gridStore.selectedItemsOnGrid[0]
    const selectedShapeId = selectedItem.metadata?.shape_id || selectedItem.attrs?.metadata?.shape_id
    const selectedParent = getParentShape(selectedShapeId) || getShapeById(selectedShapeId)
    const optionParent = getParentShape(optionShape.id) || getShapeById(optionShape.id)

    if (selectedParent && optionParent && selectedParent.prefix === optionParent.prefix) {
      // Find valid position in place or jump to nearest non-colliding nearby position
      const snap = gridStore.placementSnap?.x || 30
      const validPos = findNearbyNonCollidingPosition(selectedItem, optionShape.id, optionShape.rotation || 0, gridStore.shapeList, snap, 12, gridStore.gridPattern?.id)

      if (!validPos) {
        Toast({
          icon: 'warning',
          title: 'Cannot rotate tile',
          text: 'No space to rotate tile nearby',
          customClass: { popup: 'swal-toast-vertical' },
        })
        activeDropdownShapeId.value = null
        return
      }

      gridStore.saveState()
      const updatedMetadata = {
        ...selectedItem.metadata,
        ...(selectedItem.attrs?.metadata || {}),
        shape_id: optionShape.id,
      }
      const tempShapeForTransform = {
        ...selectedItem,
        x: validPos.x,
        y: validPos.y,
        metadata: updatedMetadata,
        attrs: {
          ...selectedItem.attrs,
          x: validPos.x,
          y: validPos.y,
          metadata: updatedMetadata,
          rotation: optionShape.rotation || 0,
        },
        rotation: optionShape.rotation || 0,
      }
      try {
        const transformedShape = await transformShapeForAdd(tempShapeForTransform)
        const shapeIndex = gridStore.shapeList.findIndex(s => s.id === selectedItem.id)
        if (shapeIndex !== -1) {
          gridStore.shapeList[shapeIndex] = transformedShape
          gridStore.selectedItemsOnGrid[0] = transformedShape
          gridStore.moveItemToTop(selectedItem.id)
          if (validPos.x !== selectedItem.x || validPos.y !== selectedItem.y) {
            gridStore.expandGridToFitShapes()
          }
          if (gridStore.gridPattern?.id === GRID_PATTERN_IDS.THREE_D) {
            gridStore.updateShapeList3D()
          }
        }
      } catch (e) {
        console.error('Failed to rotate selected item:', e)
      }
      activeDropdownShapeId.value = null
      return
    }
  }

  gridStore.updateSelectedTool(SELECTABLE_TOOLS.ADD)
  gridStore.setSelectedShape(optionShape)
  try {
    gridStore.setTempItemByCurrentSelection()
  } catch (e) {
    console.error('[3D Blocks] setTempItemByCurrentSelection failed:', e)
  }
  activeDropdownShapeId.value = null
}

const handleDocumentClick = (e: MouseEvent) => {
  if (!activeDropdownShapeId.value) return
  const target = e.target as HTMLElement | null
  if (target?.closest('.boxes-blank__shape-cell')) return
  activeDropdownShapeId.value = null
}

onMounted(() => {
  document.addEventListener('click', handleDocumentClick)
})

onUnmounted(() => {
  document.removeEventListener('click', handleDocumentClick)
})

watch(selectedDepth, () => {
  activeDropdownShapeId.value = null
})
</script>

<style scoped>
.boxes-blank {
  margin-top: 24px;
}

header {
  margin-bottom: 0;
}

.boxes-blank__toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
}

h2 {
  margin: 0;
  font-family: 'Montserrat', sans-serif;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 1.04px;
  text-transform: uppercase;
  color: #5f5e59;
}

.boxes-blank__toggle .panel-chevron {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  color: #5f5e59;
  transition: transform 0.2s ease;
}

.panel-chevron--collapsed {
  transform: rotate(-90deg);
}

.boxes-blank__content {
  margin-top: 4px;
}

.boxes-blank__subtitle {
  margin: 0 0 4px;
  font-family: 'Montserrat', sans-serif;
  font-size: 12.5px;
  font-weight: 400;
  line-height: 18.75px;
  color: #444340;
}

.boxes-blank__subtitle--unavailable {
  color: #a8a7a1;
}

.boxes-blank__grid-link {
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  color: #313558;
  text-decoration: underline;
  font-weight: 700;
  cursor: pointer;
}

.boxes-blank__grid-link:focus-visible {
  outline: 2px solid var(--studio-text);
  outline-offset: 2px;
}

.boxes-blank__grid-link:hover {
  color: #1d2342;
}

.boxes-blank--unavailable .boxes-blank__subtitle {
  border-top: 1px dashed #e4e3de;
  padding-top: 6px;
  margin-top: 6px;
}

/* Depth tabs */
.boxes-blank__depth-tabs {
  display: flex;
  gap: 5px;
  margin-bottom: 0;
}

.boxes-blank__depth-tab {
  flex: 1;
  height: 36px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #ffffff;
  color: #313558;
  border: 1px solid #000000;
  border-radius: 0;
  cursor: pointer;
  box-sizing: border-box;
  overflow: visible !important;
  position: relative;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.boxes-blank__depth-tab::after {
  display: none !important;
}

.boxes-blank__depth-tab--active {
  background-color: #313558;
  color: #ffffff;
}

.boxes-blank__depth-text {
  font-family: 'Montserrat', sans-serif;
  font-size: 17px;
  font-weight: 800;
  letter-spacing: -0.085px;
  line-height: 1;
}

.boxes-blank__depth-chevron {
  flex-shrink: 0;
  display: block;
  position: static !important;
  inset: auto !important;
}

.boxes-blank__depth-chevron--down {
  width: 7px !important;
  height: 4px !important;
  max-width: 7px !important;
  max-height: 4px !important;
}

.boxes-blank__depth-chevron--left {
  width: 4px !important;
  height: 7px !important;
  max-width: 4px !important;
  max-height: 7px !important;
}

.boxes-blank__toggle::after {
  display: none !important;
}

.boxes-blank__toggle .panel-chevron {
  position: static !important;
  inset: auto !important;
  width: 18px !important;
  height: 18px !important;
}

/* Container box */
.boxes-blank__box {
  margin-top: -1px;
  border: 1px solid #000000;
  padding: 4px 6px 12px 6px;
  background-color: #ffffff;
}

.boxes-blank__section {
  margin-top: 10px;
}

.boxes-blank__section:first-child {
  margin-top: 0;
}

.boxes-blank__row-label {
  margin: 0 0 4px 0;
  padding-left: 0;
  font-family: 'Montserrat', sans-serif;
  font-size: 12.5px;
  font-weight: 400;
  line-height: 18.75px;
  color: #7c7b76;
}

/* 3x3 Grid */
.boxes-blank__grid-3x3 {
  display: grid;
  grid-template-columns: repeat(3, 75px);
  gap: 16px;
  justify-content: center;
}

.boxes-blank__shape-cell {
  position: relative;
  width: 75px;
  height: 75px;
}

.boxes-blank__children {
  position: absolute;
  z-index: 100;
  top: calc(100% + 6px);
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  border: 1px solid #5dcbf4;
  border-radius: 4px;
  background: #ffffff;
  box-shadow: 0 3px 10px rgba(28, 28, 30, 0.14);
}

.boxes-blank__children--left {
  left: 0;
  right: auto;
}

.boxes-blank__children--center {
  left: 50%;
  transform: translateX(-50%);
}

.boxes-blank__children--right {
  right: 0;
  left: auto;
}

.boxes-blank__child-btn {
  position: relative;
  width: 60px;
  height: 60px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.12s;
}

.boxes-blank__child-btn:hover {
  opacity: 0.85;
}

.boxes-blank__child-btn--active {
  border: none;
  background: transparent;
}

.boxes-blank__row-single {
  display: flex;
  padding-left: 5px;
}

.boxes-blank__shape {
  position: relative;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  display: block;
  transition: opacity 0.12s;
}

.boxes-blank__shape:hover:not(.boxes-blank__shape--unavailable) {
  opacity: 0.85;
}

.boxes-blank__shape--unavailable {
  background-color: #d3d2ce;
  cursor: not-allowed;
}

.boxes-blank__shape--square {
  width: 75px;
  height: 75px;
}

.boxes-blank__shape--rect {
  width: 29px;
  height: 75px;
}

.boxes-blank__shape--rect-horiz {
  width: 75px;
  height: 29px;
}

.boxes-blank__shape-cell--rect-horiz {
  width: 75px;
  height: 29px;
}

.boxes-blank__child-btn--rect-vert {
  width: 29px;
  height: 75px;
}

.boxes-blank__child-btn--rect-horiz {
  width: 75px;
  height: 29px;
}

.boxes-blank__shape--cube {
  width: 25px;
  height: 25px;
}

.boxes-blank__shape-svg {
  display: block;
  width: 100%;
  height: 100%;
  pointer-events: none;
}
</style>
